import * as DialogPrimitive from '@radix-ui/react-dialog'
import type { ComponentProps, ReactNode } from 'react'
import styles from './OnboardingSurface.module.css'

/**
 * What makes an onboarding dialog a LOCK rather than a dialog.
 *
 * Spread onto a `Dialog.Content` that is already `open` inside a `modal` root.
 * Radix's modal mode does the rest — it traps focus, marks everything outside
 * the portal `aria-hidden` (so a screen reader's virtual cursor cannot reach the
 * shell it covers), locks background scroll, and restores focus on unmount.
 * These two handlers remove the ways out, which is the part a founder must not
 * have: escaping a mandatory step strands them on a shell they still cannot use,
 * with the step that gates it left open behind them.
 *
 * It is one object rather than two lines in three components because that is the
 * failure mode — drop one `preventDefault` and the lock silently becomes a
 * dialog. Spread it LAST, after a component's own props, so it cannot be
 * overridden by accident.
 */
export const lockedDialogProps: Pick<
  ComponentProps<typeof DialogPrimitive.Content>,
  'onEscapeKeyDown' | 'onInteractOutside'
> = {
  onEscapeKeyDown: (event) => event.preventDefault(),
  onInteractOutside: (event) => event.preventDefault(),
}

/**
 * The standard locked onboarding surface: a dimmed backdrop and one centred
 * card. The step overlay (steps 1–4) and the agreement both render through it,
 * and the unlock moment borrows its look — see `OnboardingSurface.module.css`.
 *
 * `title` and `description` are the dialog's accessible name and description, so
 * they are always rendered — an unnamed modal announces as "dialog" and nothing
 * else. `announceOnly` decides whether they are also *drawn*: on a surface whose
 * content is the point (a contract in a frame) a heading above it would be
 * chrome on top of the thing the founder came for, so it is spoken and not
 * shown.
 *
 * The title is an `h1` because while this is up it IS the page — there is no
 * document behind it for a lower level to sit under.
 */
export function OnboardingLockCard({
  testId,
  wide,
  announceOnly,
  eyebrow,
  title,
  description,
  children,
}: {
  testId: string
  /** Reading width, for a surface holding a document rather than a message. */
  wide?: boolean
  /** Name the surface for screen readers without drawing the text. */
  announceOnly?: boolean
  eyebrow?: ReactNode
  title: ReactNode
  description: ReactNode
  children?: ReactNode
}) {
  const drawn = (visible: string) => (announceOnly ? 'sr-only' : visible)

  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={wide ? `${styles.overlay} ${styles.overlayWide}` : styles.overlay}
          data-testid={testId}
        >
          <DialogPrimitive.Content
            className={wide ? `${styles.card} ${styles.cardWide}` : styles.card}
            {...lockedDialogProps}
          >
            {eyebrow !== undefined && <span className={styles.eyebrow}>{eyebrow}</span>}
            <DialogPrimitive.Title asChild>
              <h1 className={drawn(styles.title)}>{title}</h1>
            </DialogPrimitive.Title>
            <DialogPrimitive.Description asChild>
              <p className={drawn(styles.body)}>{description}</p>
            </DialogPrimitive.Description>
            {children}
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
